/**
 * PendingSocialsBadge — amber "to review" pill shown on a realtor row
 * when enrichment surfaced low-confidence social candidates OR when the
 * Cleanup Step 8 flow demoted a stored suspect link.
 *
 * Click → popover styled to match the normal per-platform popover
 * (SocialOutreachButtons#PlatformButton) so users get a consistent look
 * whether they're reviewing a confirmed profile or a pending candidate:
 *
 *   ┌───────────────────────────────────┐
 *   │ TikTok · demoted_suspect          │
 *   │ @somebody                         │
 *   │ handle names nobody               │
 *   │ "Demoted from stored link — …"    │
 *   │                                   │
 *   │ [↗ Open profile]                  │
 *   │ [✓ Add to profile]                │
 *   │ [✗ Not this profile]              │
 *   └───────────────────────────────────┘
 *
 * Accept → promotes the candidate to the real *_url field on the realtor.
 * Reject → drops the candidate from `pending_socials` (and, when the
 * candidate was demoted from a stored URL, writes a `demoted_rejected`
 * row to `social_review_log` so the Admin dashboard counts it toward
 * "Wrong").
 */
import React, { useState } from "react";
import {
  Facebook, Instagram, Linkedin, Twitter, Youtube,
  ExternalLink, Check, X, HelpCircle, Loader2, ShieldAlert,
} from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TikTokIcon, ThreadsIcon } from "@/components/social/PlatformIcons";
import ConfirmProfileDialog from "@/components/social/ConfirmProfileDialog";

// TikTokIcon + ThreadsIcon now live in components/social/PlatformIcons.jsx
// so any fix (e.g. Threads' fill-rule="evenodd" cutout that made the icon
// render as an invisible/blob shape on some browsers) is shared with
// SocialOutreachButtons instead of drifting between two copies.

// Map pending_socials field → the platform key used by the follow API
const platformKeyForField = (field) => ({
  facebook_url: "fb",
  instagram_url: "ig",
  linkedin_url: "li",
  twitter_url: "x",
  tiktok_url: "tt",
  youtube_url: "yt",
  threads_url: "th",
}[field] || null);

const FIELD_META = {
  facebook_url: { label: "Facebook", Icon: Facebook, color: "text-[#1877F2]" },
  instagram_url: { label: "Instagram", Icon: Instagram, color: "text-[#E1306C]" },
  linkedin_url: { label: "LinkedIn", Icon: Linkedin, color: "text-[#0A66C2]" },
  twitter_url: { label: "X / Twitter", Icon: Twitter, color: "text-slate-900" },
  tiktok_url: { label: "TikTok", Icon: TikTokIcon, color: "text-slate-900" },
  youtube_url: { label: "YouTube", Icon: Youtube, color: "text-[#FF0000]" },
  threads_url: { label: "Threads", Icon: ThreadsIcon, color: "text-slate-900" },
};

const MATCHED_VIA_LABEL = {
  keyword_only: "keyword match",
  keyword_and_name: "keyword + name match",
  brokerage_only: "brokerage match",
  location_only: "location match",
  firstname_only: "first-name match",
  lastname_partial: "last-name in handle",
  lastname_in_handle: "last-name in URL",
  fullname_in_handle: "full-name in URL",
  fullname_in_title: "full-name in title",
  site_query_plus_signal: "targeted search + signal",
  digital_card: "digital card",
};

// Given the raw matched_terms array, produce a compact quoted list for
// the review UI. Skips duplicate / empty entries and truncates long
// snippets so the popover section stays tight.
function formatMatchedTerms(terms) {
  if (!terms || !terms.length) return "";
  const seen = new Set();
  const out = [];
  for (const t of terms) {
    if (!t) continue;
    const clean = String(t).trim();
    if (!clean) continue;
    const key = clean.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(clean.length > 40 ? `${clean.slice(0, 37)}…` : clean);
    if (out.length >= 3) break;
  }
  return out.map((t) => `“${t}”`).join(", ");
}

export default function PendingSocialsBadge({ realtor, onChange, onFollowChanged, compact = false }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(realtor?.pending_socials || []);
  const [busy, setBusy] = useState(null); // URL currently being accept/reject'd

  // Keep local list in sync when the realtor prop updates from the parent
  // (e.g. after an in-place patch on a sibling row).
  React.useEffect(() => {
    setPending(realtor?.pending_socials || []);
  }, [realtor?.pending_socials]);

  if (!pending || pending.length === 0) return null;

  // Deduped icon list rendered inside the pill — one per unique field
  // that still has at least one pending candidate. Lets users see AT A
  // GLANCE which channels still need eyeballs (e.g. Facebook + Instagram).
  const uniqueFields = [];
  const seenFields = new Set();
  for (const p of pending) {
    if (!p?.field || seenFields.has(p.field)) continue;
    seenFields.add(p.field);
    uniqueFields.push(p.field);
  }

  const action = async (mode, candidate, { alsoFollow = false, confirmed = false } = {}) => {
    const url = candidate?.url;
    if (!url) return;
    setBusy(url);
    try {
      const path = mode === "accept" ? "accept" : "reject";
      // `confirm` = user eyeballed the profile and said "Correct" → the
      // backend also stamps verified_socials + logs a viewed_profile touch
      // (green tint) exactly like the verified-link flow. `follow` records
      // the follow server-side in the SAME request so there's no second
      // round-trip whose result could race the row patch.
      const body = mode === "accept" ? { url, confirm: confirmed, follow: alsoFollow } : { url };
      const { data } = await api.post(`/realtors/${realtor.id}/pending-socials/${path}`, body);
      const { platform: followedKey, followed_at: followedTs, ...doc } = data || {};
      const didFollow = !!(followedKey && followedTs);
      if (mode === "accept") {
        toast.success(didFollow ? "Added + marked followed" : "Added to profile");
      } else {
        toast.message("Rejected");
      }
      // ONE atomic row patch carrying the fresh doc (url, verified_socials,
      // outreach_log, pending_socials) AND the follow entry.
      const nextFollows = { ...(realtor?.follows || {}) };
      if (didFollow) nextFollows[followedKey] = followedTs;
      onChange?.({ ...(realtor || {}), ...doc, id: realtor.id, follows: nextFollows });
      if (didFollow) onFollowChanged?.(realtor.id, followedKey, followedTs);
      setPending(doc.pending_socials || []);
    } catch (e) {
      toast.error(`Couldn't ${mode}`, { description: formatApiError(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors ${
            compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs"
          } font-medium text-amber-800`}
          data-testid={`pending-socials-badge-${realtor.id}`}
          title={`Social profiles found by search — click to review (${pending.length})`}
        >
          {uniqueFields.length > 0 ? (
            <span className="flex items-center gap-0.5" data-testid={`pending-socials-icons-${realtor.id}`}>
              {uniqueFields.map((field) => {
                const meta = FIELD_META[field] || { label: field, Icon: HelpCircle, color: "text-amber-700" };
                const Icon = meta.Icon;
                return (
                  <Icon
                    key={field}
                    className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} ${meta.color}`}
                    data-testid={`pending-socials-icon-${field}-${realtor.id}`}
                    aria-label={meta.label}
                  />
                );
              })}
            </span>
          ) : (
            <HelpCircle className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
          )}
          to review
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)] p-2"
        data-testid={`pending-socials-popover-${realtor.id}`}
      >
        <div className="max-h-[70vh] overflow-y-auto space-y-2">
          {pending.map((p, idx) => {
            const pKey = platformKeyForField(p.field);
            const alreadyFollowed = !!(pKey && (realtor?.follows || {})[pKey]);
            return (
              <PendingCandidateSection
                key={p.url || idx}
                candidate={p}
                realtor={realtor}
                busy={busy === p.url}
                alreadyFollowed={alreadyFollowed}
                onAccept={(opts) => action("accept", p, opts)}
                onReject={() => action("reject", p)}
                onChangeDone={(doc) => {
                  onChange?.({ ...(realtor || {}), ...doc, id: realtor.id });
                  setPending(doc.pending_socials || []);
                }}
                divider={idx > 0}
              />
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Renders a single pending candidate as a block styled to mirror the
// per-platform popover from SocialOutreachButtons: platform header +
// stacked action buttons. The demoted_suspect / matched_via pill sits
// inline in the header, and the reason text ("handle names nobody",
// "Demoted from stored link — Cleanup Step 8") lives directly under it
// so the operator has full context before hitting ✓ or ✗.
function PendingCandidateSection({
  candidate, realtor, busy, alreadyFollowed,
  onAccept, onReject, divider, onChangeDone,
}) {
  const meta = FIELD_META[candidate.field] || { label: candidate.field, Icon: HelpCircle, color: "text-slate-600" };
  const Icon = meta.Icon;
  const rawVia = candidate.matched_via || "";
  const isDemoted = !!candidate.demoted_from_stored || rawVia === "demoted_suspect";
  const via = MATCHED_VIA_LABEL[rawVia] || rawVia || "";
  const matched = formatMatchedTerms(candidate.matched_terms);
  const firstName = realtor?.first_name || "this realtor";
  // Confirm-this-profile dialog reuses the shared component so this
  // popover's "Open profile" flow feels identical to the verified-link
  // first-open dialog inside SocialOutreachButtons.
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Human-readable "why is this pending?" line rendered under the handle.
  // For Step-8-demoted candidates the backend already writes a plain-English
  // reason into matched_terms (e.g. "handle 'foo' names nobody"); prefer that
  // so the user sees the exact rule that caught the link. Otherwise fall
  // back to the matched_via label or a generic string.
  let reasonLine = "";
  const firstTerm = Array.isArray(candidate.matched_terms) && candidate.matched_terms[0]
    ? String(candidate.matched_terms[0]).trim()
    : "";
  if (isDemoted && firstTerm) {
    reasonLine = firstTerm;
  } else if (via) {
    reasonLine = `matched by ${via}`;
  } else {
    reasonLine = `couldn't confirm this profile is ${firstName}`;
  }

  // Peek the profile in a new tab first, then surface the confirm dialog.
  // Mirrors SocialOutreachButtons's peek-first-then-confirm UX so users
  // can eyeball the page while deciding.
  const handleOpenProfile = () => {
    if (candidate.url) {
      window.open(candidate.url, "_blank", "noopener,noreferrer");
    }
    setConfirmOpen(true);
  };

  // "Change this link" handler — calls the same change-link endpoint as the
  // stored-URL dialog, but also passes remove_pending_url so the backend
  // removes the pending candidate in the same request.
  const changeLink = async (newUrl, { alsoFollow = false } = {}) => {
    try {
      const { data } = await api.post(`/realtors/${realtor.id}/socials/change-link`, {
        field: candidate.field,
        new_url: newUrl,
        remove_pending_url: candidate.url,
      });
      if (alsoFollow) {
        const pKey = platformKeyForField(candidate.field);
        if (pKey) {
          try {
            await api.post(`/realtors/${realtor.id}/follows/${pKey}`);
          } catch { /* follow is best-effort */ }
        }
      }
      toast.success(`${meta.label} link updated & verified${alsoFollow ? " & followed" : ""}`, {
        description: "Old link logged as incorrect — new link saved.",
      });
      setConfirmOpen(false);
      onChangeDone?.(data);
    } catch (e) {
      toast.error("Couldn't change link", { description: formatApiError(e) });
    }
  };

  // Dialog "Correct" = the user looked at the profile → accept AND verify
  // (+ log the viewed_profile touch) so the icon ends up green-tinted and
  // the confirm dialog never re-fires for this URL. The inline "Add to
  // profile" button below stays a plain accept.
  const confirmAccept = () => {
    setConfirmOpen(false);
    onAccept?.({ confirmed: true });
  };
  const confirmAcceptAndFollow = () => {
    setConfirmOpen(false);
    onAccept?.({ confirmed: true, alsoFollow: true });
  };
  const confirmReject = () => {
    setConfirmOpen(false);
    onReject?.();
  };

  return (
    <div
      className={divider ? "pt-2 border-t border-slate-100" : ""}
      data-testid={`pending-socials-row-${candidate.url}`}
    >
      {/* Header — mirrors the "Facebook / Instagram / …" title in the
          normal per-platform popover, plus a pill that surfaces the
          candidate's provenance (demoted_suspect for Step 8, matched_via
          otherwise). */}
      <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
        <div className={`inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 ${meta.color}`}>
          <Icon className="h-4 w-4" />
          <span className="text-slate-900">{meta.label}</span>
        </div>
        {isDemoted ? (
          <Badge
            variant="outline"
            className="text-[10px] gap-1 bg-amber-50 border-amber-300 text-amber-800"
            data-testid={`pending-socials-demoted-badge-${candidate.url}`}
          >
            <ShieldAlert className="h-3 w-3" /> demoted_suspect
          </Badge>
        ) : via ? (
          <Badge
            variant="outline"
            className="text-[10px] py-0 px-1 font-normal"
            data-testid={`pending-socials-via-${candidate.url}`}
          >
            {via}
          </Badge>
        ) : null}
      </div>

      {/* Candidate handle + provenance reasoning. Everything a user
          needs to decide ✓ or ✗ without leaving this popover. */}
      <div className="px-1 space-y-1">
        {candidate.handle ? (
          <div className="text-xs text-slate-700">
            <span className="font-mono font-medium text-slate-900">@{candidate.handle}</span>
          </div>
        ) : null}
        <div
          className="text-[11px] text-slate-500 leading-snug"
          data-testid={`pending-socials-reason-${candidate.url}`}
        >
          {reasonLine}
        </div>
        {matched && !isDemoted ? (
          <div
            className="text-[11px] text-slate-500 leading-snug"
            data-testid={`pending-socials-terms-${candidate.url}`}
            title={`Detected: ${matched}`}
          >
            Detected: {matched}
          </div>
        ) : null}
        {candidate.source_hit && !isDemoted ? (
          <div
            className="text-[11px] italic text-slate-500 leading-snug line-clamp-3"
            data-testid={`pending-socials-snippet-${candidate.url}`}
            title={candidate.source_hit}
          >
            “{candidate.source_hit.length > 200
              ? `${candidate.source_hit.slice(0, 197)}…`
              : candidate.source_hit}”
          </div>
        ) : null}
        {isDemoted ? (
          <div
            className="text-[11px] italic text-amber-700 leading-snug"
            data-testid={`pending-socials-demoted-note-${candidate.url}`}
          >
            Demoted from stored link — Cleanup Step 8
          </div>
        ) : null}
      </div>

      {/* Action buttons — same visual language as the normal PlatformButton
          popover (full-width ghost buttons stacked). "Open profile" now
          peeks the URL AND opens the Confirm-this-profile dialog so the
          user can commit ✓/✗ without hunting for the popover row. */}
      <div className="mt-2 space-y-0.5">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start h-8"
          onClick={handleOpenProfile}
          data-testid={`pending-socials-open-${candidate.url}`}
        >
          <ExternalLink className="h-3.5 w-3.5 mr-2" />
          Open profile
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => onAccept?.()}
          className="w-full justify-start h-8 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
          data-testid={`pending-socials-accept-${candidate.url}`}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5 mr-2" />
          )}
          Add to profile
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={onReject}
          className="w-full justify-start h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
          data-testid={`pending-socials-reject-${candidate.url}`}
        >
          <X className="h-3.5 w-3.5 mr-2" />
          Not this profile
        </Button>
      </div>

      {/* Confirm-this-profile modal — reuses the SAME dialog component
          fired by the verified-link first-open flow, so operators see one
          consistent confirm UI whether they're verifying a stored URL or
          accepting a pending candidate. Both Correct buttons perform an
          "Add to profile" (accept) — the second variant also marks the
          platform as followed in the same tap when the user isn't
          already following. */}
      <ConfirmProfileDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        busy={busy}
        platformLabel={meta.label}
        realtor={realtor}
        url={candidate.url}
        contextNote={isDemoted
          ? `Demoted from stored link — Cleanup Step 8. ${reasonLine}`
          : reasonLine}
        wrongLabel="Wrong profile"
        wrongDescription={
          <span>
            If this looks like a different {firstName} (wrong city, wrong
            brokerage, wrong face), tap <strong>Wrong profile</strong> —
            we&apos;ll drop the candidate and flag it for re-enrichment.
          </span>
        }
        correctLabel="Correct — mark verified"
        correctAndFollowLabel={!alreadyFollowed ? "Correct - Mark as followed" : undefined}
        onWrong={confirmReject}
        onCorrect={confirmAccept}
        onCorrectAndFollow={!alreadyFollowed ? confirmAcceptAndFollow : undefined}
        onChangeLink={changeLink}
        testidPrefix={`pending-socials-confirm-${candidate.url}`}
      />
    </div>
  );
}
